import { action, autorun, makeObservable, observable } from 'mobx';

import { ldClient, resolveFlag } from '../../services/launchDarkly';
import { getExtendedOffer } from '../../services/store-extended-offer';
import type { IStorePackage } from '../app/types';
import type { RootStore } from '../RootStore';

export class ExtendedOfferStore {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);

    this.isFeatureEnabled = resolveFlag('extended_store_offer_enabled', false);

    ldClient?.on('ready', () => {
      if (!ldClient) return;
      const enabled = !!ldClient.variation('extended_store_offer_enabled', false);
      this.setFeatureEnabled(enabled);
    });

    ldClient?.on('change', (changes) => {
      if ('extended_store_offer_enabled' in changes) {
        this.setFeatureEnabled(changes.extended_store_offer_enabled.current);
      }
    });

    autorun(() => {
      if (this.isFeatureEnabled && this.rootStore.authStore.authorized && this.rootStore.appStore.currentStore?.key) {
        void this.fetchExtendedOfferPackages(this.rootStore.appStore.currentStore.key);
      } else {
        this.clearExtendedOfferPackages();
      }
    });
  }

  @observable public isFeatureEnabled: boolean = false;
  @action
  private setFeatureEnabled(value: boolean) {
    this.isFeatureEnabled = value;
  }

  @observable public extendedOfferPackages: IStorePackage[] = [];
  @action
  private setExtendedOfferPackages(value: IStorePackage[]) {
    this.extendedOfferPackages = value;
  }

  @observable public loading: boolean = false;
  @action
  private setLoading(value: boolean) {
    this.loading = value;
  }

  @observable public error: boolean = false;
  @action
  private setError(value: boolean) {
    this.error = value;
  }

  @observable public currentStoreKey: string | null = null;
  @action
  private setCurrentStoreKey(value: string | null) {
    this.currentStoreKey = value;
  }

  public async fetchExtendedOfferPackages(storeKey: string): Promise<IStorePackage[]> {
    if (!storeKey) {
      this.setExtendedOfferPackages([]);
      this.setLoading(false);
      return [];
    }

    this.setCurrentStoreKey(storeKey);
    this.setLoading(true);
    this.setError(false);

    try {
      const packages = await getExtendedOffer(storeKey);
      this.setExtendedOfferPackages(packages);
      return packages;
    } catch (error) {
      this.setError(true);
      this.setExtendedOfferPackages([]);
      return [];
    } finally {
      this.setLoading(false);
    }
  }

  @action
  public clearExtendedOfferPackages() {
    this.setExtendedOfferPackages([]);
    this.setCurrentStoreKey(null);
    this.setError(false);
  }
}
